import * as vp from 'vopay';
import { Box, Table, Title, Card } from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import moment from 'moment';
import useOpenModule from '@/hooks/useOpenModule';
import useFetchContactGroups from '@/hooks/useApi/useFetchContactGroups';
import usePostDeleteGroup from '@/hooks/useApi/usePostDeleteGroup';
import { DeleteGroupDialogueHandler, DialogueHandlerProps } from './Dialogue/types';
import DeleteGroupDialogueContext, { DeleteGroupCtx } from './Dialogue/DeleteGroup/DeleteGroupContext';
import GroupGreationDialogue from './Dialogue/DeleteGroup/DeleteGroupDialogue';
import styles from './Group.module.scss';
import useFetchGroupContact from '@/hooks/useApi/useFetchGroupContact';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import { exportAsCSV } from './utils';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useTranslation } from 'react-i18next';
import { Button } from 'UI';

type GroupContainerProps = {
  type: string;
};

type MyContactGroupLists = vp.ContactGroupsLists & { index: number };

type FC = FunctionComponent<GroupContainerProps>;

const GroupContainer: FC = ({ type }: GroupContainerProps) => {
  const { userSettings } = useUserSettingsProvider();
  const history = useHistory();
  const [, setOpenModule] = useOpenModule();
  const [ContactGroups, fetchContactGroups] = useFetchContactGroups({});
  const [deleteGroupResponse, postDeleteGroup, resetDeleteGroup] = usePostDeleteGroup({});
  const [shouldDeleteGroup, setShouldDeleteGroup] = useState(false);
  const [showDeleteGroupErrorAlert, setShowDeleteGroupErrorAlert] = useState(false);
  const [deleteGroupError, setDeleteGroupError] = useState('');
  const [shouldRefetchGroup, setShouldRefetchGroup] = useState(true);
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [, , fetchGroupContact] = useFetchGroupContact({});
  const { t } = useTranslation('translations', { keyPrefix: 'Groups' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const DeleteGroup: DeleteGroupCtx = {
    showDeleteGroupErrorAlert,
    deleteGroupError,
  };
  const handlers = {
    handleDeleteGroupDialogue: (handlerProps: DialogueHandlerProps<DeleteGroupDialogueHandler>) => {
      const { type: dialogueType, state, action = 'default' } = handlerProps;
      const dialogueHandlers: DeleteGroupDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowDeleteGroupErrorAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[dialogueType][state];
      const handler = dialogueState[action] || dialogueState.default;
      handler();
    },
    onDeleteGroup: (_values: string) => {
      setShouldDeleteGroup(true);
      postDeleteGroup({
        GroupID: _values,
      });
    },
  };

  useEffect(() => {
    if (shouldRefetchGroup) {
      setTimeout(() => {
        fetchContactGroups({
          Timezone: userSettings.timezone,
        });
      }, 500);
      setShouldRefetchGroup(false);
    }
  }, [shouldRefetchGroup]);
  useEffect(() => {
    if (shouldDeleteGroup) {
      const { Success, ErrorMessage } = deleteGroupResponse;

      if (Success === true) {
        setShouldRefetchGroup(true);
      } else if (Success === false) {
        setShowDeleteGroupErrorAlert(true);
        setDeleteGroupError(ErrorMessage);
      }
      setShouldDeleteGroup(false);
      resetDeleteGroup();
    }
  }, [deleteGroupResponse]);

  const tableProps: vp.TableProps<MyContactGroupLists> = {
    id: 'group-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'name',
        header: t('Table.name'),
        align: 'left',
        template: ({ data }) => (
          <Link
            className={styles.detailBtn}
            to={{
              pathname: type === 'Payment' ? `/bulk-payment/group/${data.ID}` : `/bulk-collection/group/${data.ID}`,
              state: {
                group: data,
                type,
              },
            }}
            data-testid={`group-link-${data.index}`}
          >
            {data.GroupName}
          </Link>
        ),
      },
      {
        id: 'dateAdded',
        header: t('Table.dateAdded'),
        align: 'left',
        template: ({ data }) => moment.utc(data.DateAdded).local().format('YYYY-MM-DD HH:mm:ss'),
      },
      {
        id: 'lastModified',
        header: t('Table.lastModified'),
        align: 'left',
        template: ({ data }) => moment.utc(data.LastModified).local().format('YYYY-MM-DD HH:mm:ss'),
      },
      {
        id: 'product',
        header: t('Table.product'),
        align: 'left',
        template: ({ data }) => <span className={styles.upperCase}>{data.Service}</span>,
      },
      {
        id: 'description',
        header: t('Table.description'),
        align: 'left',
        field: 'Description',
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box gap="sm" jc="fe">
            <Button
              id={`export-group-${data.index}`}
              size="small"
              onClick={async () => {
                setDataFetchingInfo(true, 'Exporting group data', 'fullScreen');
                const groupContactResponse = await fetchGroupContact({ GroupID: data.ID });

                const myContacts = Object.values(groupContactResponse.Contacts).map((contact) => ({
                  Account: contact.Account,
                  ClientReferenceNumber: contact.ClientReferenceNumber,
                  IsActive: contact.IsActive,
                  Token: contact.Token,
                  Amount: '',
                  Currency: data.ACTCurrencies_ID === '1' ? 'USD' : 'CAD',
                  FirstName: contact.AccountHolderName?.split(' ')[0],
                  LastName: contact.AccountHolderName?.split(' ')[1],
                  AccountHolderName: contact.AccountHolderName,
                  ContactID: contact.ContactID,
                  Message: 'Successfully submitted',
                }));

                exportAsCSV(myContacts, type);
                setDataFetchingInfo(false);
              }}
            >
              {tGlobal('Buttons.export')}
            </Button>

            <Link
              to={{
                pathname: type === 'Payment' ? `/bulk-payment/edit/${data.ID}` : `/bulk-collection/edit/${data.ID}`,
                state: { group: data },
              }}
            >
              <Button id={`edit-group-${data.index}`} size="small">
                {tGlobal('Buttons.edit')}
              </Button>
            </Link>

            <Button id={`delete-group-${data.index}`} size="small" onClick={() => handlers.onDeleteGroup(data.ID)}>
              {tGlobal('Buttons.delete')}
            </Button>
          </Box>
        ),
      },
    ],
    rows: Object.values(ContactGroups?.ContactGroups || {}).map((group, index) => ({
      id: String(group.ID),
      data: { ...group, index },
    })),
  };

  return (
    <DeleteGroupDialogueContext.Provider value={DeleteGroup}>
      <Box flx="col" gapSize="36px">
        <Box jc="sb">
          <Title title={t('pageTitle')} />
          <Button
            id="create-group-button"
            colorScheme="primary"
            onClick={() => {
              type === 'Payment' ? history.push('/bulk-payment/create') : history.push('/bulk-collection/create');
              type === 'Payment' ? setOpenModule('payment') : setOpenModule('collection');
            }}
          >
            {t('createButton')}
          </Button>
        </Box>
        <Card
          style={{
            padding: '16px',
          }}
        >
          <Table {...tableProps} />
        </Card>
      </Box>
      <GroupGreationDialogue handleDialogue={handlers.handleDeleteGroupDialogue} />
    </DeleteGroupDialogueContext.Provider>
  );
};

export default GroupContainer;
